import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FilterStateGroup from '../../models/filterStateGroup.types';
import { useHistory, useParams } from 'react-router-dom';
import { getErrorPageUrl } from '../../utils/routerUrls';
import { AxiosError, AxiosResponse } from 'axios';
import { IndexedStrings, TableSelection } from '../../components/SelectableTable/SelectableTable.types';
import { Adjustment, Examinee, ExamineeListSortBy, ExamineeResponse, ExamineeStatusPl } from './ExamineeList.types';
import { useLayoutContext } from '../../Context/Layout.context';
import _ from 'lodash';
import { getExamineeList } from '../../services/images/images.service';
import { committeesArray } from '../../models/committees.types';
import { filtersActions } from '../../reducers/filtersReducer/filtersSlice';
import { DataInfoType, defaultExamineesFilter } from '../../reducers/filtersReducer/filterSlice.types';
import { RootState } from '../../store';
import { IPageInfo } from '../../models/page.types';
import { convertToFormatDate, convertToFormatTime } from '../../utils/dateFunctionUtils';
import { convertExamineesExamComitteFilter, convertStatusFilter } from './examineeTableDataUtils';

export const useExamineeTableData = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { showLoader } = useLayoutContext();
  const dataInfoType = DataInfoType.ExamineesInfo;
  const examineesDataInfo = useSelector((state: RootState) => state.filters[dataInfoType]);
  const { sheetCode: sheetCodeParam } = useParams<{ sheetCode: string }>();

  const [filterState, setFilterState] = useState<FilterStateGroup[]>([]);

  const [adjustmentsFilter, setAdjustmentsFilter] = useState<string[]>(
    examineesDataInfo.filterData.filter.adjustments.filter((val) => val === 'TAK' || val === 'NIE'),
  );
  const [adjustmentTypesFilter, setAdjustmentTypesFilter] = useState<string[]>(
    examineesDataInfo.filterData.filter.adjustmentTypes ?? [],
  );
  const [adjustmentPermissionsFilter, setAdjustmentPermissionsFilter] = useState<string[]>(
    examineesDataInfo.filterData.filter.adjustmentPermissions ?? [],
  );
  const [statusFilter, setStatusFilter] = useState<string[]>(
    convertStatusFilter(examineesDataInfo.filterData.filter.status),
  );
  const [examCommitteeFilter, setExamCommitteeFilter] = useState<string[]>(
    convertExamineesExamComitteFilter(examineesDataInfo.filterData.filter.examCommittee),
  );

  const [examinees, setExaminees] = useState<Examinee[]>([]);
  const [rawData, setRawData] = useState<ExamineeResponse | undefined>(undefined);
  const [isInitalView, setIsInitialView] = useState<boolean>(true);

  const [sortByProperty, setSortByProperty] = useState<ExamineeListSortBy>(
    examineesDataInfo.filterData.sort.sortByColumnId,
  );
  const [isDescending, setIsDescending] = useState<boolean>(examineesDataInfo.filterData.sort.isDescending);
  const [handleRefresh, setHandleRefresh] = useState<boolean>(false);

  const [filter, setFilter] = useState<IndexedStrings>(examineesDataInfo.filterData.filter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [itemsSelection, setItemsSelection] = useState<TableSelection>({ items: [] });
  const totalRecord = useRef<number>(0);

  const [adjustments, setAdjustments] = useState<{
    adjustmentTypes: Adjustment[];
    adjustmentPermissions: Adjustment[];
  }>({
    adjustmentTypes: examineesDataInfo.filterData.rawData?.adjustmentTypes ?? [],
    adjustmentPermissions: examineesDataInfo.filterData.rawData?.adjustmentPermissions ?? [],
  });
  const [adjustmentTypes, setAdjustmentTypes] = useState<Adjustment[]>(
    examineesDataInfo.filterData.rawData?.adjustmentTypes ?? [],
  );
  const [adjustmentPermissions, setAdjustmentPermissions] = useState<Adjustment[]>(
    examineesDataInfo.filterData.rawData?.adjustmentPermissions ?? [],
  );
  const [sheetCodeWithAdjustments, setSheetCodeWithAdjustments] = useState<string>('');

  const filtersApplied = Object.values(filter).filter((e) => e.length > 0).length > 0 ? true : false;

  const getPages = useCallback(
    (_page?: number, _newPageSize?: number) => {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              currentPage: _page ? _page : 0,
              pageSize: _newPageSize ? _newPageSize : examineesDataInfo.filterData.pageInfo.pageSize,
            },
          },
        }),
      );
    },
    [examineesDataInfo.filterData.pageInfo],
  );

  const handleCurrentPage = (response: IPageInfo) => {
    if (response.totalCount < examineesDataInfo.filterData.pageInfo.pageSize) {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
              currentPage: 0,
            },
          },
        }),
      );
    } else {
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            pageInfo: {
              ...response,
            },
          },
        }),
      );
    }
  };

  const handleFilterChange = useCallback(
    (name: string, value: string | string[]) => {
      typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
      if (filter[name] === value) return;

      setFilter((state) => {
        if (state[name] !== value) {
          return {
            ...state,
            [name]: value,
          };
        } else {
          return state;
        }
      });
      dispatch(
        filtersActions.updateFilter({
          filterType: dataInfoType,
          newValues: {
            filter: {
              [name]: value,
            },
            pageInfo: {
              currentPage: 0,
            },
          },
        }),
      );
      if (typeof value === 'string') {
        if (name === 'sheetCode' && value === '') {
          setFilter((state) => {
            return {
              ...state,
              adjustmentTypes: [],
              adjustmentPermissions: [],
            };
          });
          dispatch(
            filtersActions.updateFilter({
              filterType: dataInfoType,
              newValues: {
                filter: {
                  adjustmentTypes: [],
                  adjustmentPermissions: [],
                },
              },
            }),
          );
        }
      }
    },
    [setFilter, filter],
  );

  const disableChangesTillResponse = () => {
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: {
          pageInfo: {
            hasNext: false,
            hasPrevious: false,
          },
        },
      }),
    );
  };

  const refreshData = _.debounce((signal: AbortSignal) => {
    const filters = Object.keys(filter).map((key) => {
      if (Array.isArray(filter[key])) {
        return (filter[key] as string[]).join(',');
      }
      return filter[key];
    });
    if (!isValidFilter) return;
    showLoader(true);
    disableChangesTillResponse();
    getExamineeList(
      signal,
      examineesDataInfo.filterData.pageInfo.currentPage,
      examineesDataInfo.filterData.pageInfo.pageSize,
      sortByProperty,
      isDescending,
      ...(filters as string[]),
    )
      .then((res: AxiosResponse<ExamineeResponse>) => {
        setExaminees(res.data.examinees);
        if (
          res.data &&
          filter.sheetCode !== '' &&
          (filter.adjustmentTypes.length > 0 || filter.adjustmentPermissions.length > 0) &&
          (!res.data.adjustmentPermissions ||
            !res.data.adjustmentTypes ||
            res.data.adjustmentPermissions.length === 0 ||
            res.data.adjustmentTypes.length === 0)
        ) {
          const raw: ExamineeResponse = {
            ...(res.data ? res.data : examineesDataInfo.filterData.rawData!),
            adjustmentPermissions: examineesDataInfo.filterData.rawData?.adjustmentPermissions ?? [],
            adjustmentTypes: examineesDataInfo.filterData.rawData?.adjustmentTypes ?? [],
          };
          dispatch(
            filtersActions.updateFilter({
              filterType: dataInfoType,
              newValues: {
                rawData: raw,
              },
            }),
          );
          res.data ? setRawData(raw) : setRawData(undefined);
        } else {
          dispatch(filtersActions.updateFilter({ filterType: dataInfoType, newValues: { rawData: res.data } }));
          res.data ? setRawData(res.data) : setRawData(undefined);
        }
        if (res.data.page) {
          handleCurrentPage(res.data.page);
          if (isInitalView) {
            totalRecord.current = res.data.page.totalCount;
          }
        }
      })
      .catch((e: AxiosError) => {
        console.warn({ e });
        if (e?.message !== 'canceled') {
          history.push(getErrorPageUrl(e?.code ?? 500, 'admin'));
        }
      })
      .finally(() => {
        if (isInitalView) {
          setIsInitialView(false);
        }
        showLoader(false);
      });
  }, 1000);

  useEffect(() => {
    const abortController: AbortController = new AbortController();
    const { signal } = abortController;
    refreshData(signal);
    return () => {
      abortController?.abort();
    };
  }, [
    examineesDataInfo.filterData.pageInfo.currentPage,
    examineesDataInfo.filterData.pageInfo.pageSize,
    filter,
    sortByProperty,
    isDescending,
    handleRefresh,
  ]);

  useEffect(() => {
    if ((sheetCodeParam ?? '') !== '') {
      handleFilterChange('sheetCode', sheetCodeParam);
    }
  }, [sheetCodeParam]);

  const handleStatusFilterChange = useCallback(
    (value?: string) => {
      if (value && typeof value === 'string') {
        const val = statusFilter.some((f) => f === value)
          ? statusFilter.filter((f) => f !== value)
          : [...statusFilter, value];
        const valueConversion: string[] = Object.values(ExamineeStatusPl);
        const valIndex = val.map((i) => valueConversion.indexOf(i).toString());
        setStatusFilter(val);
        handleFilterChange('status', valIndex);
      } else {
        setStatusFilter([]);
        handleFilterChange('status', []);
      }
    },
    [statusFilter],
  );

  const getFilterId = (name: string) => {
    const allAdjustments = adjustments ? [...adjustments?.adjustmentTypes, ...adjustments?.adjustmentPermissions] : [];
    return allAdjustments.filter((f) => f.name === name)[0]?.id;
  };

  const handleAdjustmentsFilterChange = useCallback(
    (value?: string) => {
      if (value && typeof value === 'string') {
        if (value === 'TAK' || value === 'NIE' || !value) {
          const val = adjustmentsFilter.some((f) => f === value)
            ? adjustmentsFilter.filter((f) => f !== value)
            : [...adjustmentsFilter, value];
          setAdjustmentsFilter(val);
          handleFilterChange('adjustments', val);
        }

        if (adjustments && adjustments.adjustmentTypes.some((f) => f.id === value || f.name === value)) {
          const val = adjustmentTypesFilter.some((f) => f === value)
            ? adjustmentTypesFilter.filter((f) => f !== value)
            : [...adjustmentTypesFilter, value];
          setAdjustmentTypesFilter(val);
          handleFilterChange('adjustmentTypes', val);
        }

        if (adjustments && adjustments.adjustmentPermissions.some((f) => f.id === value || f.name === value)) {
          const val = adjustmentPermissionsFilter.some((f) => f === value)
            ? adjustmentPermissionsFilter.filter((f) => f !== value)
            : [...adjustmentPermissionsFilter, value];
          setAdjustmentPermissionsFilter(val);
          handleFilterChange('adjustmentPermissions', val);
        }
      } else {
        setAdjustmentsFilter([]);
        setAdjustmentTypesFilter([]);
        setAdjustmentPermissionsFilter([]);
        handleFilterChange('adjustments', []);
        handleFilterChange('adjustmentTypes', []);
        handleFilterChange('adjustmentPermissions', []);
      }
    },
    [adjustments, adjustmentsFilter, adjustmentTypesFilter, adjustmentPermissionsFilter],
  );

  const handleExamCommitteeFilterChange = useCallback(
    (value?: string) => {
      if (value && typeof value === 'string') {
        const val = examCommitteeFilter.some((f) => f === value)
          ? examCommitteeFilter.filter((f) => f !== value)
          : [...examCommitteeFilter, value];
        setExamCommitteeFilter(val);

        const valIndex = val.map((i) => {
          const res = committeesArray.find((e) => e.name === i);
          return res ? res.id.toString() : '';
        });
        handleFilterChange('examCommittee', valIndex);
      } else {
        setExamCommitteeFilter([]);
        handleFilterChange('examCommittee', []);
      }
    },
    [examCommitteeFilter],
  );

  useEffect(() => {
    if (!rawData) return;
    if (!rawData.examinees.length && filter.sheetCode.length && filter.sheetCode === sheetCodeWithAdjustments) return;
    setSheetCodeWithAdjustments(
      rawData?.adjustmentTypes || rawData?.adjustmentPermissions ? filter.sheetCode.toString() : '',
    );
    setAdjustmentTypes(rawData?.adjustmentTypes ?? []);
    setAdjustmentPermissions(rawData?.adjustmentPermissions ?? []);
    setAdjustments({
      adjustmentTypes: rawData?.adjustmentTypes ?? [],
      adjustmentPermissions: rawData?.adjustmentPermissions ?? [],
    });
  }, [rawData]);

  const uncheckAllFilters = () => {
    setFilterState([]);
    setExamCommitteeFilter([]);
    setStatusFilter([]);
    setAdjustmentTypesFilter([]);
    setAdjustmentPermissionsFilter([]);
    setAdjustments({ adjustmentTypes: [], adjustmentPermissions: [] });
    handleExamCommitteeFilterChange();
    handleStatusFilterChange();
    handleFilterChange('importDateFrom', '');
    handleFilterChange('importDateTo', '');
    handleAdjustmentsFilterChange('');
    setFilter(defaultExamineesFilter);
    dispatch(filtersActions.resetFilter(dataInfoType));
  };

  useEffect(() => {
    const state: FilterStateGroup[] = [];
    if (Object.values(filter).some((e) => (_.isArray(e) ? e.length > 0 : e !== ''))) {
      state.push({
        name: '',
        checkedItems: [],
      });
    }
    if (filter.examCommittee.length > 0) {
      state.push({ name: 'Komisja:', checkedItems: [...examCommitteeFilter] });
    }

    if (filter.status.length > 0) {
      state.push({ name: 'Status:', checkedItems: [...statusFilter] });
    }

    if (filter.adjustments.length > 0) {
      state.push({ name: 'Dostosowania:', checkedItems: [...adjustmentsFilter] });
    }

    if (filter.adjustmentTypes.length > 0) {
      state.push({
        name: 'Sposoby dostosowań:',
        checkedItems: adjustments
          ? adjustments.adjustmentTypes.filter((e) => adjustmentTypesFilter.includes(e.id)).map((e) => e.name)
          : [],
      });
    }

    if (filter.adjustmentPermissions.length > 0) {
      state.push({
        name: 'Uprawnienia do dostosowań:',
        checkedItems: adjustments
          ? adjustments.adjustmentPermissions
              .filter((e) => adjustmentPermissionsFilter.includes(e.id))
              .map((e) => e.name)
          : [],
      });
    }

    if (filter.importDateFrom.length > 0) {
      const date = filter.importDateFrom.toString();
      state.push({ name: 'Od:', checkedItems: [`${convertToFormatDate(date)} ${convertToFormatTime(date)}`] });
    }

    if (filter.importDateTo.length > 0) {
      const date = filter.importDateTo.toString();
      state.push({ name: 'Do:', checkedItems: [`${convertToFormatDate(date)} ${convertToFormatTime(date)}`] });
    }
    setFilterState(state);
  }, [filter, adjustments]);

  const uncheckFilterItem = (item?: string) => {
    if (item) {
      const element = filterState.find((e) => e.checkedItems.find((x) => x === item));
      if (element?.name === 'Komisja:') {
        handleExamCommitteeFilterChange(item);
      } else if (element?.name === 'Status:') {
        handleStatusFilterChange(item);
      } else if (element?.name === 'Dostosowania:') {
        handleAdjustmentsFilterChange(item);
      } else if (element?.name === 'Sposoby dostosowań:' || element?.name === 'Uprawnienia do dostosowań:') {
        handleAdjustmentsFilterChange(getFilterId(item));
      } else if (element?.name === 'Od:') {
        handleFilterChange('importDateFrom', '');
      } else if (element?.name === 'Do:') {
        handleFilterChange('importDateTo', '');
      } else {
        handleStatusFilterChange(item);
      }
    }
  };

  const setIsDescendingOrder = (order: boolean) => {
    setIsDescending(order);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: { sort: { isDescending: order } },
      }),
    );
  };

  const setSortByColumn = (column: ExamineeListSortBy) => {
    setSortByProperty(column);
    dispatch(
      filtersActions.updateFilter({
        filterType: dataInfoType,
        newValues: { sort: { sortByColumnId: column } },
      }),
    );
  };

  return {
    adjustmentsFilter,
    adjustmentTypesFilter,
    adjustmentPermissionsFilter,
    statusFilter,
    filterState,
    setFilterState,
    pageInfo: examineesDataInfo.filterData.pageInfo,
    sortByProperty,
    setSortByProperty: setSortByColumn,
    isDescending,
    setIsDescending: setIsDescendingOrder,
    setHandleRefresh,
    filter,
    setFilter,
    itemsSelection,
    setItemsSelection,
    totalRecord,
    adjustments,
    adjustmentTypes,
    adjustmentPermissions,
    sheetCodeWithAdjustments,
    setSheetCodeWithAdjustments,
    handleFilterChange,
    getPages,
    filtersApplied,
    handleStatusFilterChange,
    getFilterId,
    handleAdjustmentsFilterChange,
    examCommitteeFilter,
    setExamCommitteeFilter,
    handleExamCommitteeFilterChange,
    examinees,
    uncheckAllFilters,
    uncheckFilterItem,
  };
};
